// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LazyImage } from "@insite/mobius/index";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGoogleAnalyticsState } from "@west/App/Store/Reducers";
import Button, { ButtonPresentationProps } from "@west/MobiusOverrides/Button";
import { getDataLayer } from "@west/XDL/Utilities/tracking";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { shallowEqual } from "react-redux";

const enum fields {
    heading = "heading",
    description = "description",
    icon = "icon",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    variant = "variant",
    // Google Analytics
    sendGoogleAnalyticsEvent = "sendGoogleAnalyticsEvent",
    googleAnalyticsEventName = "googleAnalyticsEventName",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.description]: string;
        [fields.icon]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.variant]: "widget" | "item";
        // Google Analytics
        [fields.sendGoogleAnalyticsEvent]: boolean;
        [fields.googleAnalyticsEventName]: string;
    };
}

export interface SolutionsWidgetStyles {
    containerWrapper?: InjectableCss;
    icon?: LazyImageProps;
    contentWrapper?: InjectableCss;
    headerText?: TypographyProps;
    descriptionText?: TypographyProps;
    button?: ButtonPresentationProps;
}

export const solutionsWidgetStyles: SolutionsWidgetStyles = {};

const styles = solutionsWidgetStyles;

const SolutionsWidget: FC<OwnProps> = ({ fields }) => {
    const history = useHistory();
    const link = useGetLink(fields.buttonLink);
    const { isInShell } = useShellContext();
    const { xGoogleAnalytics } = useAppSelector(state => {
        return {
            xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
        };
    }, shallowEqual);

    // XNG-Changes: Google Analytics
    const gaSolutionsButtonClickHandler = () => {
        if (xGoogleAnalytics.trackGoogleAnalytics && fields.sendGoogleAnalyticsEvent && link.url) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }
            const googleAnalyticsData = {
                event: fields.googleAnalyticsEventName,
                click_text: fields.buttonLabel,
                destination: link.url,
                affiliation: xGoogleAnalytics.affiliation,
                authentication_state: xGoogleAnalytics.authenticationState,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };

    const onClick = () => {
        if (!link.url) {
            return;
        }

        if (link.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = link.url;
        } else {
            history.push(link.url);
        }
        gaSolutionsButtonClickHandler();
    };

    return (
        <StyledWrapper {...styles.containerWrapper}>
            {fields.icon && fields.variant === "widget" && (
                <LazyImage {...styles.icon} altText={fields.heading} src={fields.icon} />
            )}
            <StyledWrapper {...styles.contentWrapper}>
                <Typography forwardAs="h3" {...styles.headerText} {...{ fieldVariant: fields.variant }}>
                    {parse(fields.heading, parserOptions)}
                </Typography>
                {fields.description && (
                    <Typography {...styles.descriptionText} {...{ fieldVariant: fields.variant }}>
                        {parse(fields.description, parserOptions)}
                    </Typography>
                )}
                {fields.variant === "item" && fields.buttonLabel && (
                    <Button
                        variant="primary"
                        onClick={onClick}
                        className="button"
                        {...styles.button}
                        aria-label={fields.buttonLabel}
                    >
                        {fields.buttonLabel}
                    </Button>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SolutionsWidget,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.variant,
                displayName: "DropDown",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Widget",
                        value: "widget",
                    },
                    {
                        displayName: "Item",
                        value: "item",
                    },
                ],
                defaultValue: "widget",
                fieldType: "General",
            },
            {
                name: fields.icon,
                displayName: "Icon",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: false,
                isVisible: item => item.fields.variant === "widget",
            },
            {
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                extendedConfig: { height: 170 },
            },
            {
                name: fields.description,
                editorTemplate: "RichTextField",
                displayName: "Description",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: false,
                extendedConfig: { height: 170 },
            },
            {
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: false,
                isVisible: item => item.fields.variant === "item",
            },
            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                isVisible: item => item.fields.variant === "item",
            },
            // Google Analytics
            // XNG-Changes: google analytics
            {
                name: fields.sendGoogleAnalyticsEvent,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.googleAnalyticsEventName,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                isVisible: item => item.fields.sendGoogleAnalyticsEvent,
            },
        ],
    },
};

export default widgetModule;
